/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';
import styled from 'styled-components';

import FormLabel from './form/FormLabel';
import TextHoverDisplay from './TextHoverDisplay';

const PreviewContainer = styled.div`
  border: 1px dashed #CCC;
  padding: 15px;
  overflow-x: auto;
  overflow-y: auto;
  min-height: 100px;
  max-height: 600px;
  
  /* Override global table styles for preview table - need higher specificity than .table.table */
  /* Also need to override Bootstrap's box-shadow which creates the background effect */
  .table.table.data-preview-table tbody tr:nth-of-type(odd) {
    background-color: #FFFFFF !important;
  }
  
  .table.table.data-preview-table tbody tr:nth-of-type(odd) td {
    background-color: #FFFFFF !important;
    box-shadow: inset 0 0 0 9999px #FFFFFF !important;
  }
  
  .table.table.data-preview-table tbody tr:nth-of-type(even) {
    background-color: #e9ecef !important;
  }
  
  .table.table.data-preview-table tbody tr:nth-of-type(even) td {
    background-color: #e9ecef !important;
    box-shadow: inset 0 0 0 9999px #e9ecef !important;
  }
  
  /* Override box-shadow for highlighted columns */
  .table.table.data-preview-table tbody tr td.data-preview-override-cell {
    background-color: #f1f8f2 !important;
    box-shadow: inset 0 0 0 9999px #f1f8f2 !important;
  }
  
  .table.table.data-preview-table tbody tr td.data-preview-selected-cell {
    background-color: #f3f8ff !important;
    box-shadow: inset 0 0 0 9999px #f3f8ff !important;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

const DataPreview = ({
  fields = [],
  data = [],
  title = "Preview",
  emptyStateMessage = "Please configure your source to preview the data",
  showRowCount = true,
  cellMaxWidth = '500px',
  truncateLength = 75,
  selectedFields = [],
  highlightSelectedColumns = false,
  overrideFields = []
}) => {
  const displayData = data;
  const hasData = fields.length > 0 && data.length > 0;

  return (
    <div>
      <FormLabel>{title}</FormLabel>
      <PreviewContainer>
        {!hasData ? (
          <EmptyStateContainer>
            <FormLabel>{emptyStateMessage}</FormLabel>
          </EmptyStateContainer>
        ) : (
          <div>
            <div style={{ overflowX: 'auto', width: 'fit-content' }}>
              <Table 
                bordered
                hover
                size="sm"
                style={{ width: 'auto' }}
                className="table-sm data-preview-table"
              >
                <thead>
                  <tr>
                    {fields.map((field, index) => {
                      const isOverride = highlightSelectedColumns && overrideFields.includes(field);
                      const isSelected = !isOverride && highlightSelectedColumns && selectedFields.includes(field);
                      return (
                        <th 
                          key={index} 
                          style={{ 
                            whiteSpace: 'nowrap', 
                            padding: '0.25rem 0.5rem', 
                            fontSize: '0.875rem',
                            color: '#2d5d7e', // Consistent blue text
                            backgroundColor: isOverride ? '#e8f5e9' : (isSelected ? '#e3f2fd' : '#FFFFFF'),
                            boxShadow: isOverride ? 'inset 0 0 0 9999px #e8f5e9' : (isSelected ? 'inset 0 0 0 9999px #e3f2fd' : undefined),
                            borderLeft: isOverride ? '3px solid #2e7d32' : (isSelected ? '3px solid #2196f3' : '1px solid #dee2e6'),
                            borderRight: isOverride ? '3px solid #2e7d32' : (isSelected ? '3px solid #2196f3' : '1px solid #dee2e6'),
                            fontWeight: (isOverride || isSelected) ? 'bold' : 'normal'
                          }}
                        >
                          {field}
                          {isOverride && <span style={{ marginLeft: '5px', color: '#2e7d32' }}>●</span>}
                          {isSelected && <span style={{ marginLeft: '5px', color: '#2196f3' }}>✓</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {displayData.map((row, rowIndex) => {
                    return (
                      <tr key={rowIndex}>
                        {fields.map((field, fieldIndex) => {
                          const cellValue = row[fieldIndex] || '';
                          const isLongText = cellValue.length > truncateLength;
                          const isFieldOverride = highlightSelectedColumns && overrideFields.includes(field);
                          const isFieldSelected = !isFieldOverride && highlightSelectedColumns && selectedFields.includes(field);
                          
                          // Use row background unless overridden by selection/override
                          let cellBgColor = 'inherit'; // Inherit from tr background set by CSS
                          let cellBoxShadow = undefined;
                          if (isFieldOverride) {
                            cellBgColor = '#f1f8f2'; // rgb(241, 248, 242)
                            cellBoxShadow = 'inset 0 0 0 9999px #f1f8f2';
                          } else if (isFieldSelected) {
                            cellBgColor = '#f3f8ff'; // rgb(243, 248, 255)
                            cellBoxShadow = 'inset 0 0 0 9999px #f3f8ff';
                          }
                          
                          return (
                            <td 
                              key={fieldIndex}
                              className={isFieldOverride ? 'data-preview-override-cell' : (isFieldSelected ? 'data-preview-selected-cell' : '')}
                              style={{ 
                                padding: '0.25rem 0.5rem', 
                                fontSize: '0.875rem',
                                maxWidth: cellMaxWidth,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#2d5d7e', // Consistent blue text
                                backgroundColor: cellBgColor !== 'inherit' ? cellBgColor : undefined,
                                boxShadow: cellBoxShadow,
                                borderLeft: isFieldOverride ? '3px solid #2e7d32' : (isFieldSelected ? '3px solid #2196f3' : '1px solid #dee2e6'),
                                borderRight: isFieldOverride ? '3px solid #2e7d32' : (isFieldSelected ? '3px solid #2196f3' : '1px solid #dee2e6')
                              }}
                            >
                              {isLongText ? (
                                <TextHoverDisplay
                                  truncatedText={cellValue.substring(0, truncateLength)}
                                  fullText={cellValue}
                                  displayTruncated={true}
                                  canShowCopyToClipboard={false}
                                  minWidth={250}
                                />
                              ) : (
                                cellValue
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            {showRowCount && data.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d' }}>
                Showing {data.length} rows
              </div>
            )}
          </div>
        )}
      </PreviewContainer>
    </div>
  );
};

DataPreview.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.arrayOf(PropTypes.array),
  title: PropTypes.string,
  emptyStateMessage: PropTypes.string,
  showRowCount: PropTypes.bool,
  cellMaxWidth: PropTypes.string,
  truncateLength: PropTypes.number,
  selectedFields: PropTypes.arrayOf(PropTypes.string),
  highlightSelectedColumns: PropTypes.bool,
  overrideFields: PropTypes.arrayOf(PropTypes.string)
};

DataPreview.defaultProps = {
  fields: [],
  data: [],
  title: "Preview",
  emptyStateMessage: "Please configure your source to preview the data",
  showRowCount: true,
  cellMaxWidth: '500px',
  truncateLength: 75,
  selectedFields: [],
  highlightSelectedColumns: false,
  overrideFields: []
};

export default DataPreview;
